const DEFAULT_KOSTEBEK = {
  50: "#eef3d3",
  100: "#e3ebb6",
  200: "#dde7a7",
  300: "#d7e399",
  400: "#d1df8a",
  500: "#ccdb7c",
  600: "#c6d76d",
  700: "#b2c262",
  800: "#9eac57",
  900: "#8b974c",
};

const hexToRgb = (hex) => {
  const cleaned = String(hex).replace("#", "").trim();
  const full =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((c) => c + c)
          .join("")
      : cleaned;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const n = parseInt(full, 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0"))
    .join("")}`;

const mix = (from, to, weight) => ({
  r: Math.round(from.r * (1 - weight) + to.r * weight),
  g: Math.round(from.g * (1 - weight) + to.g * weight),
  b: Math.round(from.b * (1 - weight) + to.b * weight),
});

const WHITE = { r: 255, g: 255, b: 255 };
const BLACK = { r: 0, g: 0, b: 0 };
const ON_PRIMARY_LIGHT = "#ffffff";
const ON_PRIMARY_DARK = "#374151"; // gray-700 — matches default solid button text

const channelLuminance = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance 0–1 */
export const getRelativeLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
};

/**
 * Text/icon color for primary backgrounds.
 * Light primaries (e.g. Mole green) → dark text; saturated/dark (e.g. #007aff) → white.
 */
export const getOnPrimaryColor = (hex) => {
  const luminance = getRelativeLuminance(hex);
  if (luminance === null) return ON_PRIMARY_DARK;
  return luminance > 0.45 ? ON_PRIMARY_DARK : ON_PRIMARY_LIGHT;
};

/**
 * Build a 50–900 palette from a primary hex (500 = primary).
 */
export const buildPaletteFromPrimary = (primaryColor) => {
  const primary = hexToRgb(primaryColor);
  if (!primary) return null;
  return {
    50: rgbToHex(mix(primary, WHITE, 0.92)),
    100: rgbToHex(mix(primary, WHITE, 0.8)),
    200: rgbToHex(mix(primary, WHITE, 0.65)),
    300: rgbToHex(mix(primary, WHITE, 0.45)),
    400: rgbToHex(mix(primary, WHITE, 0.25)),
    500: rgbToHex(primary),
    600: rgbToHex(mix(primary, BLACK, 0.12)),
    700: rgbToHex(mix(primary, BLACK, 0.24)),
    800: rgbToHex(mix(primary, BLACK, 0.36)),
    900: rgbToHex(mix(primary, BLACK, 0.48)),
  };
};

/**
 * Apply primary color as CSS vars for kostebek scale + on-primary contrast.
 * No-op when primaryColor is missing/invalid (keeps default palette).
 */
export const applyWhitelabelTheme = (primaryColor) => {
  if (!primaryColor || !String(primaryColor).trim()) {
    clearWhitelabelTheme();
    return;
  }
  const palette = buildPaletteFromPrimary(primaryColor);
  if (!palette) return;
  const root = document.documentElement;
  Object.entries(palette).forEach(([level, value]) => {
    root.style.setProperty(`--kostebek-${level}`, value);
  });
  // Contrast against solid button bg (600) and primary (500)
  const onPrimary = getOnPrimaryColor(palette[600] || primaryColor);
  root.style.setProperty("--kostebek-on-primary", onPrimary);
  root.classList.add("wl-primary");
};

export const clearWhitelabelTheme = () => {
  const root = document.documentElement;
  Object.keys(DEFAULT_KOSTEBEK).forEach((level) => {
    root.style.removeProperty(`--kostebek-${level}`);
  });
  root.style.removeProperty("--kostebek-on-primary");
  root.classList.remove("wl-primary");
};

export default applyWhitelabelTheme;
